import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Checkbox,
  Divider,
} from '@mui/material';
import ConsultantDTO from '@src/adapters/dto/consultant/ConsultantDTO';
import HospitalUserDTO from '@src/adapters/dto/hospital/HospitalUserDTO';
import { useGetConsultantRecordsLazy } from '@src/adapters/hooks/consultants/useGetConsultantRecords';
import OrganisationImageFallback from '@src/components/organisation/OrganisationImageFallback';
import RolesAutoComplete from '@src/components/user/RolesAutoComplete';
import SearchBox from '@src/components/utilities/SearchBox';
import { UserRoles } from '@src/constants/UserRoles';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ReactComponent as PersonIcon } from '@src/assets/icons/person_filed_backgroud.svg';
import useHospitalJoinRequestLazy from '@src/adapters/hooks/hospital/useHospitalJoinRequest';
import { useSnackbar } from 'notistack';
import { HospitalRequestStatus } from '@src/constants/HospitalJoinRequestStatus';
import { UserStatus } from '@src/constants/UserStatus';
import { StatusUpdate } from '@src/constants/StatusUpdate';

interface AccessRequestModalProps {
  open: boolean;
  onClose: () => void;
  user: HospitalUserDTO;
  onStatusChange?: (userId: string, status: StatusUpdate) => void;
}

const STEPS = {
  REVIEW_REQUEST: 1,
  CONFIRM: 2,
};

function AccessRequestModal({
  open,
  onClose,
  user,
  onStatusChange,
}: AccessRequestModalProps) {
  const [step, setStep] = useState<number>(STEPS.REVIEW_REQUEST);
  const [roles, setRoles] = useState<string[]>(user.roles);
  const { execute: searchConsultanRecords } = useGetConsultantRecordsLazy();
  const { execute: updateJoinRequest } = useHospitalJoinRequestLazy();
  const [consultants, setConsultants] = useState<ConsultantDTO[]>([]);
  const [isUserSelected, setIsUserSelected] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const isConsultant = roles.includes('Consultant');

  const [searchRecords, setSearchRecords] = useState<string | null | undefined>(
    user.registrationNumber
  );

  const valueRef = useRef<string>(user.registrationNumber ?? '');

  const handleValueChange = (value: string) => {
    valueRef.current = value;
  };

  const handleSearch = () => {
    setSearchRecords(valueRef.current);
  };

  const findConsultantRecords = useCallback(async () => {
    if (!searchRecords) return;

    const records = await searchConsultanRecords({
      gmcNumber: searchRecords,
    });
    setConsultants(records ?? []);
  }, [searchConsultanRecords, searchRecords]);

  useEffect(() => {
    if (open && step === STEPS.CONFIRM) {
      findConsultantRecords();
    }
  }, [findConsultantRecords, open, step]);

  const handleJoinRequest = async (action: 'ACCEPT' | 'REJECT') => {
    const selectedConsultant = consultants.find(
      (consultant) => consultant.gmcReference === isUserSelected
    )?.id;

    await updateJoinRequest({
      action,
      hospitalJoinRequestId: user.joinRequestId,
      linkToConsultantId: selectedConsultant,
      registrationNumber: searchRecords ?? user.registrationNumber,
      roles: roles,
    });

    enqueueSnackbar(`User ${action.toLocaleLowerCase()}ed`, {
      variant: 'success',
    });
  };

  const handleAccept = async () => {
    const selectedConsultant = consultants.find(
      (consultant) => consultant.gmcReference === isUserSelected
    );

    if (selectedConsultant) {
      const namesMatch =
        selectedConsultant.firstName?.trim().toLowerCase() ===
          user.firstName?.trim().toLowerCase() &&
        selectedConsultant.lastName?.trim().toLowerCase() ===
          user.lastName?.trim().toLowerCase();

      if (!namesMatch) {
        enqueueSnackbar(
          `${user.firstName} ${user.lastName} doesn't match the selected record ${selectedConsultant.firstName} ${selectedConsultant.lastName}`,
          { variant: 'warning' }
        );
        return;
      }
    }

    await handleJoinRequest('ACCEPT');
    onStatusChange?.(user.userId, UserStatus.Active);
    onClose();
  };

  const handleReject = async () => {
    await handleJoinRequest('REJECT');
    onStatusChange?.(user.userId, HospitalRequestStatus.Rejected);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          pt: 3,
          pb: 0,
        }}>
        <Stack direction={'row'} gap={2} alignItems={'center'}>
          <PersonIcon />

          <Typography variant="h5">
            {step === STEPS.REVIEW_REQUEST
              ? 'Respond to access request'
              : 'Search for consultant record'}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        {step === STEPS.REVIEW_REQUEST && (
          <>
            <Stack
              direction="row"
              spacing={2}
              pb={1.25}
              sx={{
                py: 1.8,
              }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  First name
                </Typography>
                <Typography variant="body1">{user.firstName}</Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Last name
                </Typography>
                <Typography variant="body1">{user.lastName}</Typography>
              </Box>
            </Stack>

            <Box>
              <Divider sx={{ mx: -3 }} />
            </Box>

            <Stack direction="row" spacing={2} py={1.8}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Professional body
                </Typography>
                <Typography variant="body1">
                  {user.professionalBodyName}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Registration number
                </Typography>
                <Typography variant="body1">
                  {user.registrationNumber}
                </Typography>
              </Box>
            </Stack>

            <Box>
              <Divider sx={{ mx: -3 }} />
            </Box>

            <Box
              sx={{
                py: 1.8,
              }}>
              <Typography variant="body2" color="text.secondary">
                Request message
              </Typography>
              <Typography>{user.message}</Typography>
            </Box>

            <Box>
              <Divider sx={{ mx: -3 }} />
            </Box>

            <Box
              sx={{
                py: 3,
              }}>
              <RolesAutoComplete
                required
                selection={setRoles}
                userRoles={roles}
                options={UserRoles}
                variant="outlined"
                size="small"
                isRequired
              />
            </Box>

            <Box>
              <Divider sx={{ mx: -3 }} />
            </Box>
          </>
        )}

        {step === STEPS.CONFIRM && (
          <>
            <Stack
              pt={1.25}
              pb={3}
              direction={'row'}
              alignItems={'center'}
              gap={3}
              borderBottom={'1px solid'}
              borderColor={'divider'}>
              <SearchBox
                title="GMC"
                search={setSearchRecords}
                onValueChange={handleValueChange}
                size="small"
                useSecondaryColor={false}
                defaultValue={searchRecords ?? ''}
              />
              <Button
                variant="contained"
                sx={{ width: 64, height: 30 }}
                onClick={handleSearch}>
                Search
              </Button>
            </Stack>
            {consultants.length > 0 ? (
              consultants.map((consultant) => (
                <Stack
                  key={consultant.gmcReference}
                  direction={'row'}
                  alignItems={'center'}
                  justifyContent={'center'}
                  pt={1}>
                  <Stack direction={'row'} alignItems={'center'} flex={1}>
                    <Checkbox
                      checked={isUserSelected === consultant.gmcReference}
                      onChange={(e) =>
                        setIsUserSelected(
                          e.target.checked ? consultant.gmcReference : null
                        )
                      }
                    />
                    <OrganisationImageFallback src={null} alt={'User Image'} />
                    <Typography variant="subtitle1" sx={{ ml: 2 }}>
                      {consultant.firstName} {consultant.lastName}
                    </Typography>
                  </Stack>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      {consultant.gmcReference}
                    </Typography>
                  </Box>
                </Stack>
              ))
            ) : (
              <Box sx={{ py: 3 }}>
                <Typography variant="body1">
                  We couldn't find an existing record for this consultant.
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 0.5 }}>
        {step === STEPS.REVIEW_REQUEST && (
          <>
            <Button onClick={onClose} variant="outlined" size="large">
              Cancel
            </Button>
            <Button
              onClick={handleReject}
              variant="contained"
              size="large"
              color="error">
              Reject
            </Button>
            {isConsultant ? (
              <Button
                onClick={() => {
                  if (roles.length > 0) setStep(STEPS.CONFIRM);
                }}
                size="large"
                variant="contained">
                Continue
              </Button>
            ) : (
              <Button
                onClick={handleAccept}
                color={!isConsultant ? 'success' : undefined}
                variant="contained"
                size="large"
                disabled={roles.length === 0}
                sx={!isConsultant ? { color: 'white' } : undefined}>
                Approve Access
              </Button>
            )}
          </>
        )}

        {step === STEPS.CONFIRM && (
          <>
            <Button
              onClick={() => {
                setStep(STEPS.REVIEW_REQUEST);
                setConsultants([]);
              }}
              variant="outlined"
              size="large">
              Back
            </Button>
            <Button
              onClick={handleAccept}
              variant="contained"
              size="large"
              disabled={roles.length === 0}>
              {!isUserSelected ? 'Create as new consultant' : 'Approve Access'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default AccessRequestModal;
